import { yupResolver } from "@hookform/resolvers/yup";
import * as yup from "yup";

import { CategoryApi, useCategoriesOptions } from "api/categories";
import { PaziaFormButton, PaziaInput, PaziaSelect } from "components/atoms";
import { PaziaCheckbox } from "components/atoms/PaziaCheckbox/PaziaCheckbox";
import { FormProvider, useForm } from "react-hook-form";
import { PaziaFromQuill } from "components/atoms/PaziaQuill/PaziaFromQuill";

interface CategoryFormProps {
  onSubmit: (data: CategoryFormValues) => void;
  category?: CategoryApi;
}
export const CategoryForm = ({ onSubmit, category }: CategoryFormProps) => {
  const { loading: categoriesLoading, options: parentOptions } = useCategoriesOptions(category?.id);

  const methods = useForm({
    resolver: yupResolver(schema),
    context: { savedSlug: category?.slug },
    defaultValues: {
      [FORM_NAMES.NAME]: category?.name ?? "",
      [FORM_NAMES.FRIENDLY_NAME]: category?.friendlyName ?? "",
      [FORM_NAMES.SLUG]: category?.slug ?? "",
      [FORM_NAMES.DESCRIPTION]: category?.description ?? "",
      [FORM_NAMES.PARENT_ID]: category?.parentId ?? "",
      [FORM_NAMES.SORT_ORDER]: category?.sortOrder ?? 0,
      [FORM_NAMES.EXCLUDE_FROM_SITEMAP]: category?.excludeFromSitemap ?? false,
    },
  });
  const { handleSubmit } = methods;

  return (
    <FormProvider {...methods}>
      <form onSubmit={handleSubmit(onSubmit)} className="grid max-w-xl gap-2">
        <PaziaSelect
          name={FORM_NAMES.PARENT_ID}
          label="Kategoria nadrzędna"
          options={[{ value: "", label: "Brak (kategoria główna)" }, ...parentOptions]}
          disabled={categoriesLoading}
        />
        <PaziaInput name={FORM_NAMES.NAME} label="Nazwa" required />
        <PaziaInput name={FORM_NAMES.FRIENDLY_NAME} label="Nazwa przyjazna" required />
        <PaziaInput
          name={FORM_NAMES.SLUG}
          label="Slug"
          required
          placeholder="np. kursy-it"
          tooltip="Fragment adresu w sklepie: małe litery bez polskich znaków, cyfry i myślniki"
        />
        <PaziaInput name={FORM_NAMES.SORT_ORDER} label="Kolejność" type="number" />
        <PaziaCheckbox
          name={FORM_NAMES.EXCLUDE_FROM_SITEMAP}
          label="Wyklucz z sitemapy (kursy z tej kategorii nie będą indeksowane przez wyszukiwarki)"
        />
        <PaziaFromQuill name={FORM_NAMES.DESCRIPTION} label="Opis" />
        <PaziaFormButton />
      </form>
    </FormProvider>
  );
};

const FORM_NAMES = {
  NAME: "name",
  FRIENDLY_NAME: "friendlyName",
  SLUG: "slug",
  DESCRIPTION: "description",
  PARENT_ID: "parentId",
  SORT_ORDER: "sortOrder",
  EXCLUDE_FROM_SITEMAP: "excludeFromSitemap",
} as const;
// Same rule as the API (CategorySlugRules): one clean URL segment of the storefront path
export const CATEGORY_SLUG_PATTERN = /^[a-z0-9]+(-[a-z0-9]+)*$/;
const CATEGORY_SLUG_MESSAGE = "Slug może zawierać tylko małe litery bez polskich znaków, cyfry i pojedyncze myślniki";

const schema = yup
  .object({
    [FORM_NAMES.NAME]: yup.string().required(),
    [FORM_NAMES.FRIENDLY_NAME]: yup.string().required(),
    [FORM_NAMES.SLUG]: yup
      .string()
      .required()
      // A slug saved before the rule existed may be kept as is (the API allows that too); a new one must follow it
      .test("slug-format", CATEGORY_SLUG_MESSAGE, (value, ctx) =>
        Boolean(value && (value === ctx.options.context?.savedSlug || CATEGORY_SLUG_PATTERN.test(value))),
      ),
    [FORM_NAMES.DESCRIPTION]: yup.string(),
    [FORM_NAMES.PARENT_ID]: yup
      .mixed()
      .transform((value) => (value === "" ? null : Number(value)))
      .nullable(),
    [FORM_NAMES.SORT_ORDER]: yup.number().default(0),
    [FORM_NAMES.EXCLUDE_FROM_SITEMAP]: yup.boolean().default(false),
  })
  .required();
export type CategoryFormValues = yup.InferType<typeof schema>;
